import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
// Copyright   : 2025 semantics GmbH
// Date        : $Date: 2025-11-01 16:43:09 +0100 (Sat, 01 Nov 2025) $
// Author      : Dirk Rothe
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 157706 $
// Purpose     : access restriction dashboard panels
// =============================================================================
// ---Third-party imports
import React from 'react';
// ---Framework imports
import log from 'uix/logging';
import { useApi, useState } from 'uix/hooks';
import { DashSegment } from 'uix/components/grid';
import { XMenu } from 'uix/components';
import { useColumnsForNode } from 'uix/db/hooks';
import { PropertyTable } from 'uix/db/proplist';
// ---Local imports
// ---Exports
const ttStyle = {
    fontFamily: "monospace, Menlo, Consolas, 'Courier New', monospace",
    fontSize: '0.9em',
    backgroundColor: '#f5f5f5',
    color: '#d6336c',
    padding: '0.15em 0.4em',
    borderRadius: '4px',
    border: '1px solid #e1e1e1',
    whiteSpace: 'nowrap'
};
const TT = ({ children })=>/*#__PURE__*/ _jsx("tt", {
        style: ttStyle,
        children: children
    });
const RestrictionPanel = ({ node, i18n, dconfig })=>{
    let [showInfo, setShowInfo] = useState(false);
    let [test_ip, set_test_ip] = useState('');
    let { data } = useApi('restriction/evaluateGuestUserAccess', {
        otid: node.otid,
        test_ip
    }, {
        condition: showInfo
    });
    let guestUserAccess = data?.guestUserAccess || {};
    let { user_groups = [], user_ip, allowed = '', reason = '', restriction_name, restriction_groups = [], restriction_networks = [] } = guestUserAccess;
    let groups = user_groups.filter((g)=>g != 'guest').map((g, i)=>/*#__PURE__*/ _jsxs("span", {
            children: [
                /*#__PURE__*/ _jsx(TT, {
                    children: g
                }),
                ' '
            ]
        }, i));
    if (!groups.length) {
        groups = /*#__PURE__*/ _jsx(TT, {
            children: "[no groups]"
        });
    }
    let networks = restriction_networks.map((n, i)=>/*#__PURE__*/ _jsxs("span", {
            children: [
                /*#__PURE__*/ _jsx(TT, {
                    children: n
                }),
                ' '
            ]
        }, i));
    if (restriction_networks.length == 0) {
        networks = /*#__PURE__*/ _jsx(TT, {
            children: "[no networks configured]"
        });
    }
    if (restriction_groups.includes('*')) {
        // custom public group
        networks = /*#__PURE__*/ _jsx(TT, {
            children: "*"
        });
    }
    let rgroups = restriction_groups.filter((g)=>g != '*').map((g, i)=>/*#__PURE__*/ _jsxs("span", {
            children: [
                /*#__PURE__*/ _jsx(TT, {
                    children: g
                }),
                ' '
            ]
        }, i));
    rgroups = rgroups.length ? /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            "that are configured for IP-groups ",
            rgroups
        ]
    }) : null;
    let infoBlock = data ? /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    "Guest-User ",
                    /*#__PURE__*/ _jsx(TT, {
                        children: user_ip
                    }),
                    " has IP-groups ",
                    groups,
                    ". This resolves to state",
                    ' ',
                    /*#__PURE__*/ _jsx(TT, {
                        children: allowed.split(':').at(-1)
                    }),
                    " due to reason ",
                    /*#__PURE__*/ _jsx(TT, {
                        children: reason.split(':').at(-1)
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                style: {
                    marginTop: '10px'
                },
                children: [
                    "The active restriction ",
                    /*#__PURE__*/ _jsx(TT, {
                        children: restriction_name
                    }),
                    " is accessible by the ip-networks",
                    ' ',
                    networks,
                    rgroups
                ]
            })
        ]
    }) : null;
    let columnList = useColumnsForNode({
        fields: [
            'restriction.value',
            'restriction.inherited',
            'restriction.inherited_otid',
            'oc.restrictionstate'
        ],
        nodeData: node,
        showEmpty: false
    });
    let menu = /*#__PURE__*/ _jsxs(XMenu, {
        children: [
            /*#__PURE__*/ _jsx(XMenu.Input, {
                disabled: !showInfo,
                placeholder: user_ip,
                debounce: 1000,
                onValueChange: set_test_ip,
                title: "Change Test-IP"
            }),
            /*#__PURE__*/ _jsx(XMenu.Toggle, {
                icon: "info",
                value: showInfo,
                set: setShowInfo,
                content: "Guest"
            })
        ]
    });
    return /*#__PURE__*/ _jsxs(DashSegment, {
        header: i18n.get('restriction'),
        menu: menu,
        children: [
            /*#__PURE__*/ _jsx(PropertyTable, {
                columnList: columnList
            }),
            infoBlock
        ]
    });
};
export const NodeRestrictions = {
    Comp: RestrictionPanel,
    //~ order: 99,
    align: 'right',
    enabled: ({ dconfig })=>dconfig.accessLevels.on,
    datatypes: [
        'metadata'
    ]
};
